import { Field, WrappedFieldInputProps, WrappedFieldProps } from 'redux-form';
import cn from 'classnames';
import { rm } from 'utils';
import { PaymentMethod as PaymentMethodModel } from 'models/PaymentMethod';
import { RadioButton } from 'components/Shared/Form';
import { PartPaymentItem } from 'models/PartPaymentItem';
import styles from './styles.scss';
import { CreditWarning } from './creditWarning';
import paymentIcons from './paymentIcons';
import { PartPayment } from 'views/CheckoutPage/themes/BGH/payment/partPayment';

interface PaymentMethodProps {
  method: PaymentMethodModel;
  input: WrappedFieldInputProps;
  reset: () => void;
  isActive: boolean;
  disabled: boolean;
  dispatchGtmChangePaymentMethod: (newPaymentMethod: string) => void;
}

const PART_PAYMENT_METHOD = 'svea_payment_part'

const useShouldPaymentMethodShowPriceText = ({ method }: Pick<PaymentMethodProps, 'method'>) => {
  const isValidMethod = method.isInvoiceFeeEnabled || method.commonName === PART_PAYMENT_METHOD
  return isValidMethod
};

export const PaymentMethod = ({
  method,
  input,
  reset,
  isActive,
  disabled,
  dispatchGtmChangePaymentMethod
}: PaymentMethodProps) => {
  const shouldPaymentMethodShowPriceText = useShouldPaymentMethodShowPriceText({ method });
  const isPartPaymentMethod = Boolean(method.partPaymentItems && method.partPaymentItems.length);
  const isCampaignMethod = Boolean(method.campaignText);
  const paymentMethodCost = getPriceText(method);
  const showPaymentMethodCost =
    (method.price > 0 && paymentMethodCost !== '0') || shouldPaymentMethodShowPriceText;

  const handleOnChange = (newValue: string) => {
    dispatchGtmChangePaymentMethod(newValue);
    input.onChange(newValue);
  };

  return (
    <div>
      <RadioButton
        size="small"
        {...input}
        onChange={handleOnChange}
        checked={isActive}
        disabled={disabled}
        value={method.commonName}
        className={styles.paymentRadioButtonWrapper}
        radioButtonClassName={cn(styles.paymentRadioButton, {
          [styles.active]: isActive
        })}
        label={
          <>
            <span className={styles.displayName}>{method.displayName}</span>
            <div className={styles.paymentTypes}>
              {method.paymentType.map(icon => paymentIcons(icon))}
            </div>
            {showPaymentMethodCost && (
              <span
                className={cn(styles.price, {
                  [styles.campaign]: !!method.campaignText || shouldPaymentMethodShowPriceText
                })}
              >
                {paymentMethodCost}
              </span>
            )}
          </>
        }
      />
      {isCampaignMethod && isActive && (
        <div className={styles.campaignDescription}>{getCampaignDescription(method)}</div>
      )}
      {isPartPaymentMethod && isActive && (
        <Field
          required
          name="partPaymentItem"
          component={renderPartPaymentItems}
          items={method.partPaymentItems}
          reset={reset}
          dispatchGtmChangePaymentMethod={dispatchGtmChangePaymentMethod}
          paymentMethod={method.commonName}
        />
      )}
      {method.showCreditWarning && isActive && <CreditWarning />}
    </div>
  );
};

interface RenderPartPaymentItemsProps extends WrappedFieldProps {
  items: PartPaymentItem[];
  reset: () => void;
  dispatchGtmChangePaymentMethod: (newPaymentMethod: string) => void;
  paymentMethod: string;
}
const renderPartPaymentItems = ({
  items,
  reset,
  input,
  dispatchGtmChangePaymentMethod,
  paymentMethod
}: RenderPartPaymentItemsProps) => (
  <PartPayment
    items={items}
    input={input}
    reset={reset}
    dispatchGtmChangePaymentMethod={dispatchGtmChangePaymentMethod}
    paymentMethod={paymentMethod}
  />
);

function getPriceText(method: PaymentMethodModel) {
  if (method.isInvoiceFeeEnabled) {
    return rm('checkout_invoice_cost');
  }

  if (method.commonName === PART_PAYMENT_METHOD) {
    if (method.partPaymentItems.length === 0) {
      return 0;
    }

    return String(
      rm(
        'checkout_partpayment_from',
        method.partPaymentItems[method.partPaymentItems.length - 1].costPerMonthText,
        ''
      )
    );
  }

  if (method.campaignText) {
    return method.campaignText;
  }

  return method.priceText;
}

function getCampaignDescription(method: PaymentMethodModel) {
  return method.campaignFees
    ? rm(
        'checkout_partpayment_interest_rd3',
        method.campaignFees.interest,
        method.campaignFees.startFeeText,
        method.campaignFees.monthFeeText
      )
    : '';
}